import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

/**
 * Session cache (Checkpoint D root-cause fix, Q12).
 *
 * The Supabase auth client serializes token access with a Web Lock
 * (navigator.locks, "lock:sb-<ref>-auth-token"). Calling
 * `supabase.auth.getSession()` on EVERY API request meant concurrent requests
 * (dashboard/tab Promise.all) raced for that lock and stole it from each other,
 * throwing "Lock was released because another request stole it" — which left
 * the profile fetch and list fetches rejected and pages spinning forever.
 *
 * Fix: subscribe ONCE to auth changes and cache the session here. Callers read
 * the cache synchronously (getCachedSession / getAccessToken) instead of calling
 * getSession() per request, so nothing contends for the lock in the hot path.
 */
let cachedSession = null;
let markReady;
const authReady = new Promise((resolve) => { markReady = resolve; });

supabase.auth.onAuthStateChange((_event, session) => {
  cachedSession = session ?? null;
  if (markReady) { markReady(); }
});

// One seed read in case the initial onAuthStateChange is slow; this is a SINGLE
// getSession at module load, never per-request, so it does not cause contention.
supabase.auth.getSession()
  .then(({ data }) => { if (cachedSession == null) cachedSession = data?.session ?? null; })
  .catch(() => {})
  .finally(() => { if (markReady) { markReady(); } });

export const getCachedSession = () => cachedSession;
export const getAccessToken = () => cachedSession?.access_token ?? null;
// Resolves once the first auth state (or the seed read) has landed. Awaiting
// this shared promise from many concurrent requests is free — it is not a lock.
export const waitForAuthReady = () => authReady;

// Auth helpers
export const signInWithGoogle = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      scopes: 'openid profile email',
      redirectTo: window.location.origin + '/auth/callback'
    }
  });
  return { data, error };
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  return { error };
};

export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser();
  return { user, error };
};

export const getSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  return { session, error };
};

// Subscribe to auth changes
export const onAuthStateChange = (callback) => {
  return supabase.auth.onAuthStateChange(callback);
};
